import { inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ApiService } from '../api/api.service';
import { environment } from 'src/environments/environment';
import { UtilService } from '../util/util.service';
import { VwoService } from '../vwo/vwo.service';
import { VariablesService } from '../variables/variables.service';
import { ActivatedRoute } from '@angular/router';
import { ICart, ICartCampaign, ICartParams, ICartResponse, IContribute, IContributeParams, IOrderParams, IPaymentConfig, IPaymentOptions, ISendParams, ISendResponse, PAYMENT_DEFAULT_CONFIG } from '../../models/payment-v3.model';
import { IOrder } from 'src/app/common/payment/models/order.model';
import { EventsService } from '../events/events.service';
import { ISipCause } from '../../models/sip-cause.model';
import { Currency } from '../../models/currency-list';
import { AuthService } from '../auth/auth.service';
import { IUser } from '../../models';
import { PusherService } from '../pusher/pusher.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ScriptLoaderService } from '../script-loader/script-loader.service';

declare const Stripe: any;
declare const Juspay: any;
declare const Razorpay: any;

@Injectable({
  providedIn: 'root'
})
export class PaymentV3Service {
  private readonly actRoute = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly events = inject(EventsService);
  private readonly dialog = inject(MatDialog);
  private readonly pusher = inject(PusherService);
  private readonly sheet = inject(MatBottomSheet);
  private readonly util = inject(UtilService);
  private readonly vars = inject(VariablesService);
  private readonly vwo = inject(VwoService);
  private readonly scriptLoader = inject(ScriptLoaderService);
  private readonly zone = inject(NgZone);

  config$ = new BehaviorSubject<Partial<IPaymentConfig>>(undefined);
  config: IPaymentConfig = { ...PAYMENT_DEFAULT_CONFIG };
  dispalyCartAmount: number = 0;
  cartAmount: number = 0;
  loading = false;
  isQR = false;
  otdContributeHitUrl = '';
  sipContributeHitUrl = '';
  otdOptionsHitUrl = '';
  sipOptionsHitUrl = '';
  captchaResponse = '';
  cartParams?: ICartParams;
  cartRes?: ICartResponse;
  sipCartRes?: ICartResponse;
  cartCampaign?: ICartCampaign;
  otdContributeRes?: IContribute;
  sipContributeRes?: IContribute;
  isUpiMode: boolean = false;
  orderParams?: IOrderParams;
  orderRes?: IOrder;
  otdOptionsRes?: IPaymentOptions;
  sipOptionsRes?: IPaymentOptions;
  supportedCheckouts = ['standard', 'custom'];
  paymentType: 'standard' | 'custom' = 'custom';
  toggleCheckoutType$ = new Subject<'standard' | 'custom'>();
  sendApiParams?: ISendParams;
  sendApiRes?: ISendResponse;
  skipRedirectAfterPayment = false;
  paymentStartedFrom = '';
  otdPaymentGateway = '';
  sipPaymentGateway = '';
  isPaypalOtd = false;
  isPaypalSip = false;
  paypalCardFields: any;
  disablePayBtn = false;
  errorInPaymentApi: 'cart' | 'order' | 'send' | '' = '';
  skipCaptcha = false;
  upiQrCode = '';
  paytmQrCode = '';
  userData: IUser;
  paymentStartPossition = '';
  disableAll$ = new Subject<boolean>();
  onPayBtnClick$ = new Subject<'UPI' | 'CARD' | 'NB' | 'PAYTM' | 'WALLET' | 'UPIRECURRING' | 'Emandate' | 'WALLET_RECURRING'>();
  paymentDialogRef: MatDialogRef<any>;

  constructor() {
    this.config$.subscribe(res => {
      if (!res) return;
      this.config = { ...this.config, ...res };
      let amount = this.config.selectedAmount;
      let sipAmount = this.config.sipSelectedAmount;
      if ('selectedAmount' in res || 'sipSelectedAmount' in res || 'isSip' in res || 'tipAmount' in res || 'sipTipAmount' in res) {
        // this.config.tipAmount = this.getTipAmount(this.config.tipAmountPerc, this.config.selectedAmount);
        // amount = (this.config?.selectedAmount || 0) + this.config.tipAmount;
        // this.config.sipTipAmount = this.getTipAmount(this.config.sipTipAmountPerc, this.config.sipSelectedAmount);
        // sipAmount = (this.config?.sipSelectedAmount || 0) + this.config.sipTipAmount;
        if (this.config.isSip) {
          this.cartAmount = sipAmount;
          this.dispalyCartAmount = sipAmount;
        } else {
          this.cartAmount = amount;
          this.dispalyCartAmount = amount;
        }
      }
    });
    this.vars.userData.subscribe(u => {
      if (!u) return;
      this.userData = u;
    });
  }

  getTipAmount = (perc: number = 0, amt: number = 0) => {
    return Math.ceil(((perc) / 100) * (amt || 0));
  }

  resetPaymentConfig() {
    this.cartParams = this.cartRes = this.cartCampaign = this.otdContributeRes = this.sipContributeRes = this.orderParams = this.orderRes = this.sendApiParams = this.sendApiRes = this.otdOptionsRes = this.sipOptionsRes = undefined;
    this.captchaResponse = '';
    this.config = { ...PAYMENT_DEFAULT_CONFIG };
  }

  getContributeData(data: IContributeParams, config: IPaymentConfig) {
    return new Promise((resolve, reject) => {
      const { isSip } = config;
      if (!data?.currency || (!isSip && !data?.campaign_id)) { reject(false); return; }
      const qParams = this.actRoute.snapshot.queryParams;
      const contributePayload = this.util.removeEmptyFromObject({
        ...data,
        update_multiplier: !isSip ? true : null,
        no_pan_change: !isSip ? true : null,
        is_story: ['s'].includes(this.vars.origin) && !isSip ? true : null,
        origin: this.vars.origin,
        device: this.vars.deviceType,
        pcurrency: data?.previousCurrency || null,
        slab_ab: this.vwo.slab_AB() || null,
        skipmin: qParams?.skipmin ? true : null,
        ...this.util.utm_url_obj
      });
      delete contributePayload?.checkout_type;
      delete contributePayload?.previousCurrency;

      const url = isSip ? environment.APP.API_URLS.GET_RECURRING_CONTRIBUTE_DETAILS : environment.APP.API_URLS.CONTRIBUTE_DETAILS_PAYMENT_FORM(data.campaign_id);
      const urlToCompare = `${url}?${this.util.objectToUrlString(contributePayload)}`;
      if ((!isSip && this.otdContributeHitUrl === urlToCompare) || (isSip && this.sipContributeHitUrl === urlToCompare)) {
        if (isSip && this.sipContributeRes) {
          resolve(config);
        } else if (!isSip && this.otdContributeRes) {
          resolve(config);
        } else {
          reject(false);
        }
        return;
      }
      if (isSip) {
        this.sipContributeHitUrl = urlToCompare;
      } else {
        this.otdContributeHitUrl = urlToCompare;
      }
      this.vars.showSkeletonFor.slab = true;
      this.loading = true;
      this.disableAll$.next(true);
      this.api.request('get', url, contributePayload).subscribe({
        next: (res: any) => {
          if (!res?.data?.data) { this.loading = false; this.disableAll$.next(false); reject(false); return; }
          const constributeData: IContribute = {
            ...res.data.data,
            timestamp: new Date().getTime(),
            selected_currency: data?.currency
          };
          try {
            if (qParams?.min_donation && this.vars.numberOnlyRegex.test(qParams?.min_donation)) {
              constributeData.min_donation = +qParams?.min_donation;
            }
          } catch (error) { }
          if (config.enableMaintenanceFee && constributeData.mandate_fee) {
            config.sipTipAmount = constributeData.mandate_fee;
          }
          let updateConfig: Partial<IPaymentConfig> = {
            pCurrency: constributeData.selected_currency
          };
          if (isSip) {
            this.sipContributeRes = constributeData;
            updateConfig = {
              ...updateConfig,
              sipContributeRes: constributeData,
              sipSelectedAmount: constributeData.default_amount,
            }
            this.vars.supportedSipCurrency = this.vars.currencies.filter(val => constributeData?.supported_currency?.includes(val.currency));
          } else {
            this.otdContributeRes = constributeData;
            updateConfig = {
              ...updateConfig,
              otdContributeRes: constributeData,
              selectedAmount: constributeData.default_amount,
            }
            this.vars.supportedCurrency = this.vars.currencies.filter(val => constributeData?.supported_currency?.includes(val.currency));
          }
          this.config$.next(updateConfig);
          config = { ...config, ...updateConfig };
          this.sendDonationSlabEvent(config);
          this.loading = false;
          this.disableAll$.next(false);
          this.vars.showSkeletonFor.slab = false;
          resolve(config);
        },
        error: (err: any) => {
          this.loading = false;
          this.disableAll$.next(false);
          this.otdContributeHitUrl = '';
          this.vars.showSkeletonFor.slab = false;
          this.util.openSnackBar(err?.error?.message, 'error');
          reject(false);
        }
      });
    });
  }

  sendDonationSlabEvent(config: IPaymentConfig) {
    const { isSip, otdContributeRes, sipContributeRes } = config;
    const slabs = (isSip ? sipContributeRes : otdContributeRes).slabs;
    const ePayload = {
      eventName: 'Donation_Slabs',
      event_type: 'campaign',
      info_1: JSON.stringify(slabs),
      info_2: config.pCurrency
    };
    this.events.sendSystemEvent(ePayload).subscribe(_ => _);
  }

  getOptions(data: { currency: string; phone: string | number; campaignId: number; indian: boolean; amount: number; recurring?: any; isUPI?: boolean }) {
    return new Promise((resolve, reject) => {
      const qParams = this.actRoute.snapshot.queryParams;
      const eParams = {
        currency: data.currency,
        amount: data.amount,
        mobile_no: data.phone,
        is_mobile: this.vars.isMobile ? 1 : 0,
        campaign_id: data.campaignId,
        indian: data?.indian,
        ...this.util.utm_url_obj
      }
      if (data?.recurring) eParams['recurring'] = 1;
      if (data?.isUPI) eParams['mode'] = 'UPI';
      if (qParams?.utm_source === 'internal_stories_otd_calling') eParams['internal_mini_story'] = 1;
      const url = environment.APP.API_URLS.GET_PAYMENT_OPTIONS + '?' + this.util.objectToUrlString(eParams);
      const hitUrl = eParams?.recurring ? this.sipOptionsHitUrl : this.otdOptionsHitUrl;
      const optionsRes = this.getOptionsExistData(eParams?.recurring);
      if (hitUrl === url && optionsRes) {
        resolve(optionsRes);
        return;
      }
      if (eParams?.recurring) {
        this.sipOptionsHitUrl = url;
      } else {
        this.otdOptionsHitUrl = url;
      }
      this.loading = true;
      this.disableAll$.next(true);
      this.api.request('get', environment.APP.API_URLS.GET_PAYMENT_OPTIONS, eParams).subscribe({
        next: (res: any) => {
          if (this.vwo.new_upi_AB() && res?.data?.options) res.data.options['vpa_collect_enabled'] = false;
          if (eParams?.recurring) {
            this.sipOptionsRes = res?.data;
          } else {
            this.otdOptionsRes = res?.data;
          }
          this.loading = false;
          this.disableAll$.next(false);
          resolve(res?.data);
        },
        error: (err: any) => {
          this.loading = false;
          this.disableAll$.next(false);
          resolve(false);
        }
      });
    });
  }

  getOptionsExistData(isSip) {
    return isSip ? this.sipOptionsRes : this.otdOptionsRes;
  }

  createCart(data: ICartParams, config: IPaymentConfig) {
    return new Promise((resolve, reject) => {
      const { isSip, sipCauseArray, isSipStory } = config;
      const minDonation = isSip ? this.sipContributeRes.min_donation : this.otdContributeRes.min_donation;
      if (data.donated_amount < minDonation) {
        this.util.openSnackBar(`please select amount greater than or equal to ${minDonation}`, 'error');
        return;
      } else if (data.donated_amount > this.vars.maxDonationAmount) {
        this.util.openSnackBar(`please select amount less than or equal to ${this.vars.maxDonationAmount}`, 'error');
        return;
      }
      const oldCart: ICartResponse = this.util.storage.getFromSession(isSip ? 'newSipCartData' : 'newCartData');
      if (!this.util.isOlderThan(oldCart?.cart?.timestamp || 0, 10)) {
        const { cart } = oldCart;
        const fieldsToCompare: (keyof typeof data)[] = [
          'donated_amount',
          'campaign_id',
          'donor_name',
          'donor_address',
          'donor_extension',
          'donor_phone',
          'donor_email'
        ];
        const isSame = fieldsToCompare.every(field => cart[field] === data[field]) && cart.recurring === isSip;
        if (isSame) {
          if (isSip) {
            this.sipCartRes = oldCart;
          } else {
            this.cartRes = oldCart;
          }
          resolve(oldCart);
          return;
        }
      }
      const ePayload: ICartParams = {
        ...data,
        device: this.vars.deviceType
      }
      if (isSip) {
        ePayload.recurring = 1;
        ePayload.split = false;
        ePayload.sip_cause = sipCauseArray || [{ sip_cause: this.vars.default_sip_cause, amount: data.donated_amount }];
      }
      if (isSipStory) {
        ePayload.cause_amount = 1;
      }
      const url = environment.APP.API_URLS.POST_AMOUNT_DETAILS(data.campaign_id);
      this.loading = true;
      this.disableAll$.next(true);
      this.api.request('post', url, '', this.util.removeEmptyFromObject(ePayload)).subscribe({
        next: async (res: any) => {
          if (!res?.data?.cart) return;
          if (this.vars.isDummyEmail) {
            this.events.updateCTprofile({ name: ePayload?.donor_name, email: ePayload?.donor_email });
          }
          this.upiQrCode = '';
          this.paytmQrCode = '';
          const exrtaCartData = this.util.removeEmptyFromObject({
            timestamp: new Date().getTime(),
            supportFee: this.vars.supportFee || null,
            vendor: this.vars.selected_insurance?.vendor || null,
            plan: this.vars.selected_insurance?.plan || null,
            plan_id: this.vars.selected_insurance?.plan_id || null,
            showKCart: this.vars.showKCart || null,
            campaign_products: this.vars.showKCart ? (this.vars.selectedKCart || null) : null,
            donor_phone: this.vars.isAutoLogin ? ePayload.donor_phone : null,
            indian: ePayload?.indian,
            sip_cause: ePayload?.recurring ? ePayload?.sip_cause : null,
            sip_cause_str: ePayload?.recurring ? this.util.jsonToString(ePayload?.sip_cause) : null,
            onlySipCauseArr: ePayload?.recurring ? this.totalSip(ePayload?.sip_cause, config.pCurrency).only_cause_list : null,
            ngo_sip: config.ngoSip ? 1 : null,
            initPossition: config?.possition
          });
          res.data.cart = { ...res.data.cart, ...exrtaCartData };
          const cartData = res.data;
          this.vars.userPhoneExt = cartData?.cart?.donor_extension || ePayload?.donor_extension;
          this.vars.userPhoneNumber = cartData?.cart?.donor_phone || ePayload?.donor_phone;
          if (this.userData && !this.userData?.phone_1) {
            this.userData.extension = this.vars.userPhoneExt;
            this.userData.phone_1 = this.vars.userPhoneNumber;
            this.util.setUserData(this.userData);
          }
          if (this.vars.isVariableLogin && cartData?.cart?.donor_entity_details_id && ePayload.recurring) {
            this.util.setSessionUser(`${cartData?.cart?.donor_entity_details_id}SipCart`, cartData);
            this.util.setSessionUser(cartData?.cart?.donor_entity_details_id, { full_name: ePayload.donor_name, email: ePayload.donor_email, extension: ePayload.donor_extension, phone_1: ePayload.donor_phone });
          }
          if (ePayload.recurring) {
            this.sipCartRes = cartData;
            this.util.storage.checkFromSession('newSipCartData', this.sipCartRes);
          } else {
            this.cartRes = cartData;
            this.util.storage.checkFromSession('newCartData', this.cartRes);
          }
          if (cartData.hasOwnProperty('token')) {
            this.vars.loginMethod = 'cart';
            const newUser = await this.auth.userLogin(cartData);
          }
          this.contributeInitiatedEvents(config, cartData);
          this.eventAfterCreatingCart(config, cartData);
          this.captchaResponse = '';
          this.loading = false;
          this.disableAll$.next(false);
          resolve(cartData);
        },
        error: (err: any) => {
          this.captchaResponse = '';
          this.loading = false;
          this.disableAll$.next(false);
          this.util.openSnackBar(err?.error?.message, 'error');
          resolve(false);
        }
      });
    });
  }

  createOrder(data: IOrderParams, config: IPaymentConfig) {
    return new Promise((resolve) => {
      if (data?.vpa && this.vars.recommendedVpa && data?.vpa !== this.vars.recommendedVpa) {
        this.events.sendSystemEvent({
          eventName: 'vpa_changed',
          info_1: 'new vpa = ' + data?.vpa,
          info_2: 'old vpa = ' + this.vars.recommendedVpa
        }).subscribe(_ => _);
      }
      const { isSip } = config;
      const cartData = isSip ? this.sipCartRes : this.cartRes;
      const { cart } = cartData;
      const ePayload = {
        campaign_id: cart?.campaign_id || this.vars.fundraiser.id,
        cart_id: cart?.id,
        gateway: isSip ? this.sipOptionsRes.gateway : this.otdOptionsRes.gateway,
        bin: data.mode === 'CARD' && !data.customer_id && data.card_number_fill ? data.card_number_fill.substring(0, 6) : null,
        page_type: this.vars.pageName || this.vars.campaignType,
        ab_testname: this.events.ab.abtestname || '',
        ab_value: this.events.ab.abvalue || '',
        is_indian: cart?.indian || '',
        sip_cause: isSip ? cart?.sip_cause : null,
        support_campaign_id: this.vars.fundraiser?.support_campaign?.id,
        cause_amount: isSip ? 1 : null,
        is_multi_patient_campaign: this.vars.donateMultiPatient ? true : null,
        bulkdonation: cart?.bulkdonation,
        vendor: cart?.vendor,
        plan: cart?.plan,
        plan_id: cart?.plan_id,
        critical_illness: this.vars.selectedIllness?.illnessAmount,
        coupon_code: this.vars.couponDetails?.coupon_code,
        campaign_products: cart.campaign_products,
        ...data,
        ...this.util.utm_url_obj
      }
      this.orderParams = this.util.removeEmptyFromObject(ePayload);

      delete ePayload.card_name_fill;
      delete ePayload.card_number_fill;
      delete ePayload.card_month_fill;
      delete ePayload.card_year_fill;
      delete ePayload.card_cvv_fill;

      delete ePayload.auth_mode;
      delete ePayload.account_no;
      delete ePayload.ifsc_code;
      delete ePayload.bank_code;
      delete ePayload.bank_name;

      const url = environment.APP.API_URLS.PLACE_ORDER(cartData?.cart?.campaign_id || this.vars.fundraiser.id);
      this.loading = true;
      this.disableAll$.next(true);
      this.api.request('post', url, {}, this.util.removeEmptyFromObject(ePayload)).subscribe({
        next: async (res: any) => {
          if (!res?.data) return;
          if (data.auto_pay_m1) {
            res.data['auto_pay_m1'] = data.auto_pay_m1;
          }
          this.orderRes = res?.data;
          this.eventAfterCreatingOrder(this.orderRes, cartData);
          this.util.storage.checkFromSession(this.orderRes.order_id, { cartData: cartData, orderData: this.orderRes });
          if (this.orderRes.payment_gateway.match('stripe') && this.orderRes.payment_mode !== 'LINK' && !data?.oneTapPay) {
            resolve(await this.stripePayment(config));
            return;
          } else if (this.orderRes.payment_gateway.match('juspay') && this.orderRes.payment_mode === 'CARD') {
            resolve(await this.juspayPayment());
            return;
          } else {
            resolve(await this.sendOrder(config));
          }
          // this.loading = false;
          // this.disableAll$.next(false);
        },
        error: (err: any) => {
          this.loading = false;
          this.disableAll$.next(false);
          if (err?.error?.message?.match('Invalid card number')) {
            this.vars.oneTapPayError.next('invalid');
          } else if (err?.error?.message?.match('Limit exceeded')) {
            this.vars.oneTapPayError.next('limit_exceed');
          }
          if (this.orderParams?.vpa) {
            this.vars.showTimer = false;
            this.vars.upi_payment_status.next('failed');
          }
          resolve(false);
        }
      });
    });
  }

  sendOrder(config: IPaymentConfig, data?: ISendParams) {
    return new Promise((resolve) => {
      const { cart, campaign } = config.isSip ? this.sipCartRes : this.cartRes;
      this.vars.tyParams = {
        ...this.actRoute.snapshot.queryParams,
        oi: this.orderRes.order_id,
        id: this.orderRes.campaign_id,
        or: config?.origin || this.vars.pageName,
        ct: campaign.custom_tag,
        url: this.util.router.url.split('?')[0],
        payment_v: 'v2',
        ...this.util.utm_url_obj
      };
      this.util.storage.addSessionData('payment_return_params', { k_params: JSON.stringify(this.vars.tyParams), ...this.vars.tyParams });
      const domain = this.util.domain_details.domain_url + environment.base_ref;
      this.sendApiParams = this.util.removeEmptyFromObject({
        order_id: this.orderRes.order_id,
        auth_mode: this.orderParams?.auth_mode,
        account_no: this.orderParams?.account_no,
        ifsc_code: this.orderParams?.ifsc_code,
        bank_name: this.orderParams?.bank_name,
        bank_code: this.orderParams?.bank_code,
        return_url: domain + `payment-route?${this.util.objToUrlString(this.vars.tyParams)}`,
        ...data
      });
      this.loading = true;
      this.disableAll$.next(true);
      this.api.request('post', environment.APP.API_URLS.SEND_ORDER, {}, this.sendApiParams).subscribe({
        next: (res: any) => {
          this.sendApiRes = res?.data;
          if (!this.sendApiRes) { resolve(false); return; }
          const { payment_gateway, payment_mode, payment_mode_value, private_key } = this.orderRes;
          switch (true) {
            case (this.sendApiRes?.charged):
              if (this.sendApiRes?.status === 'success') {
                let activeSips = this.util.getActiveSip(this.util.getUserData()?.listsubscriptions);
                if (this.vars.selectedOtdSipAmt && !activeSips?.includes('medical')) {
                  this.otdToSip(this.orderRes);
                } else {
                  this.goToThankYouPage(this.orderRes);
                }
              }
              break;

            case (payment_mode === 'LINK' && this.sendApiRes?.params?.client_secret):
              this.config$.next({ sendApiResponse: this.sendApiRes });
              break;

            case (payment_mode_value === 'DESKTOPQR'):
              if (payment_mode === 'UPI') {
                this.upiQrCode = this.sendApiRes.params.upi.message;
              } else if (payment_mode === 'PAYTM') {
                this.paytmQrCode = this.sendApiRes.params.paytm.message;
              }
              this.pusher.activateUPIPusher(this.orderRes);
              break;

            case (['razorpay', 'razorpay_emandate'].includes(payment_gateway)):
              if (this.vars.isMobile && this.sendApiRes?.UPI && this.sendApiRes?.url && !private_key) {
                this.util.nativeNavigation(this.sendApiRes.url);
                this.vars.upiTimer = true;
              } else {
                resolve(this.razorpayCustomCheckout(config));
                return;
              }
              break;

            case (this.sendApiRes?.UPI):
              if (['phonepe', 'payu', 'phonepe_recurring', 'payu_recurring'].includes(payment_gateway)) {
                if (this.vars.isMobile && this.sendApiRes?.url) {
                  this.util.nativeNavigation(this.sendApiRes.url);
                } else {
                  this.vars.upiTimer = true;
                }
                this.pusher.vpaTxnStatus({
                  order_id: this.orderRes.order_id,
                  origin: this.vars.tyParams.or
                });
              } else if (this.vars.isMobile) {
                this.util.nativeNavigation(this.sendApiRes.url);
                this.vars.upiTimer = true;
              }
              break;

            case ((payment_mode_value === 'VPA' && this.sendApiRes?.pg_txn_id) ? true : false):
              this.pusher.vpaTxnStatus({
                order_id: this.orderRes.order_id,
                origin: this.vars.tyParams.or
              });
              this.vars.upiTimer = true;
              break;

            default:
              this.redirectToPaymentGateway();
              resolve(this.sendApiRes);
              return;
          }
          this.loading = false;
          this.disableAll$.next(false);
          this.vars.isOrderLoading = false;
          resolve(this.sendApiRes);
        },
        error: (err: any) => {
          this.loading = false;
          this.disableAll$.next(false);
          this.vars.showTimer = false;
          if (this.orderRes.payment_gateway === 'phonepe') {
            this.vars.upi_payment_status.next('failed');
          }
          resolve(false);
        }
      });
    });
  }

  redirectToPaymentGateway() {
    const { params } = this.sendApiRes;
    if (params) {
      Object.entries(params).forEach(([key, value]: [string, string]) => {
        if (this.orderParams.hasOwnProperty(value)) {
          this.sendApiRes.params[key] = this.orderParams[value];
        }
      });
    }
    if (!this.sendApiRes?.url) {
      this.loading = false;
      this.vars.isOrderLoading = false;
      this.vars.showTimer = false;
      this.util.openSnackBar(this.sendApiRes?.message || 'something went wrong please try again', 'error');
      this.disableAll$.next(false);
      return;
    }

    const existingForm = document.getElementById('payment_gateway_form');
    if (existingForm) existingForm.remove();

    const frm = document.createElement('form');
    frm.method = this.sendApiRes?.method || 'POST';
    frm.action = this.sendApiRes.url;
    frm.id = 'payment_gateway_form';
    frm.className = 'juspay_inline_form';
    if (params) {
      Object.entries(params).forEach(([key, value]: [string, string]) => {
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = key;
        field.className = key;
        field.value = value;
        frm.appendChild(field);
      });
    }
    document.body.appendChild(frm);
    if (this.orderRes.payment_gateway?.toLowerCase() === 'juspay' && this.orderRes.payment_mode === 'CARD') {
      console.log('Juspay submit setup');
      this.juspayFormSubmit();
      // If Juspay manages form submission, don't double-submit
      (document.getElementById('payment_gateway_form') as HTMLFormElement)?.submit();
      return;
    }
    frm.submit();
  }

  juspayFormSubmit(): void {
    Juspay.Setup({
      payment_form: '#payment_form',
      success_handler: (status: any) => {
        console.log('✅ Payment Success', status);
        this.goToThankYouPage(this.orderRes);
      },
      error_handler: (error_code: string, error_message: string, bank_error_code: string, bank_error_message: string, gateway_id: string) => {
        console.error('❌ Payment Failure', {
          error_code,
          error_message,
          bank_error_code,
          bank_error_message,
          gateway_id
        });
      }
    });
  }


  stripePayment(config: IPaymentConfig) {
    return new Promise(async (resolve) => {
      await this.scriptLoader.newLoad('stripeV2');
      if (typeof Stripe === 'undefined') {
        resolve(this.stripePayment(config));
        return;
      }
      const options = this.getOptionsExistData(config.isSip);
      if (options?.setting?.stripe?.key) {
        resolve(this.getStripeToken(config));
      } else {
        this.loading = false;
        this.disableAll$.next(false);
        this.util.openSnackBar('Something went wrong please try again', 'error');
        resolve(true);
      }
    });
  }

  getStripeToken(config: IPaymentConfig) {
    return new Promise(async (resolve) => {
      const options = this.getOptionsExistData(config.isSip);
      Stripe.setPublishableKey(options?.setting?.stripe?.key);
      try {
        const token = await this.createStripeCardToken();
        this.sendOrder(config, { token: token });
      } catch (err: any) {
        this.util.openSnackBar(err?.message, 'error');
        this.loading = false;
        this.disableAll$.next(false);
      }
      resolve(true);
    });
  }

  createStripeCardToken(data: IOrderParams = this.orderParams) {
    return new Promise((resolve, reject) => {
      try {
        const stipePaylad = {
          number: data?.card_number_fill,
          cvc: data?.card_cvv_fill,
          exp_month: data?.card_month_fill,
          exp_year: data?.card_year_fill
        };
        Stripe.card.createToken(stipePaylad, (status: any, response: any) => response?.error ? reject(response.error) : resolve(response.id));
      } catch (error) {
        reject(false);
      }
    });
  }

  juspayPayment() {
    return new Promise((resolve) => {
      resolve(true);
    });
  }

  razorpayCustomCheckout(config: IPaymentConfig) {
    return new Promise(async (resolve) => {
      await this.scriptLoader.newLoad('razorpay');
      if (typeof Razorpay === 'undefined') {
        resolve(await this.razorpayCustomCheckout(config));
        return;
      }
      const { isSip } = config;
      const cartData = isSip ? this.sipCartRes : this.cartRes;
      const { setting } = this.getOptionsExistData(config.isSip);
      const razorpay = new Razorpay({
        key: setting.razorpay.key,
        image: `${environment.APP.IMAGE_DOMAIN}/images/logo-light-bg.svg`
      });
      const payload = {
        amount: this.sendApiRes?.params?.amount,
        currency: this.sendApiRes?.params?.currency?.toUpperCase() || null,
        email: this.sendApiRes?.params?.email || cartData?.cart?.donor_email,
        contact: this.sendApiRes?.params?.contact || `${cartData?.cart?.donor_extension}${cartData.cart.donor_phone}`,
        order_id: this.sendApiRes?.params?.recurring_order_id || this.sendApiRes?.params?.order_id,
        customer_id: this.sendApiRes?.params?.customer_id || null,
        method: '',
        notes: {
          order_id: this.orderRes.order_id,
          camp_id: this.orderRes.campaign_id
        }
      };
      if (isSip) {
        payload['recurring'] = 1;
      }

      if (this.orderRes.payment_mode === 'CARD') {
        payload.method = 'card';
        payload['card'] = {
          name: this.orderRes.donor_first_name,
          number: this.orderParams.card_number_fill,
          cvv: this.orderParams.card_cvv_fill,
          expiry_month: this.orderParams.card_month_fill,
          expiry_year: this.orderParams.card_year_fill
        };
      } else if (this.orderRes.payment_mode === 'NB') {
        payload.method = 'netbanking';
        payload['bank'] = this.sendApiRes?.params?.bankcode;
      } else if (this.orderRes.payment_mode === 'Emandate') {
        let donorName: string = this.orderRes.donor_first_name;
        if (donorName.length < 3) {
          donorName += ' Ketto';
        }
        payload.method = 'emandate';
        payload['auth_type'] = 'netbanking';
        payload['bank_account[account_type]'] = 'savings';
        payload['bank_account[name]'] = donorName;
        payload['bank'] = this.sendApiParams.bank_code;
        payload['bank_account[account_number]'] = this.sendApiParams.account_no;
        payload['bank_account[ifsc]'] = this.sendApiParams.ifsc_code;
      } else if (['UPI', 'UPIRECURRING'].includes(this.orderRes.payment_mode) && this.orderRes.checksum) {
        payload.method = 'upi';
        payload['vpa'] = this.orderParams.vpa;
      } else if (this.orderRes.payment_mode === 'WALLET') {
        payload.method = 'wallet';
        payload['wallet'] = this.orderRes.payment_mode_value?.toLowerCase();
      }

      razorpay.createPayment(payload);
      if (this.orderRes.payment_mode !== 'UPI') {
        razorpay.on('payment.success', (resp) => {
          this.loading = false;
          this.disableAll$.next(false);
          this.vars.isOrderLoading = false;
          Object.assign(this.vars.tyParams, resp);
          this.util.router.navigate(['/new/payment-route'], { queryParams: this.vars.tyParams });
          this.dialog.closeAll();
          this.sheet.dismiss();
        });
      }
      razorpay.on('payment.error', (error) => {
        this.razorpayFailedPayment(error?.error);
      });
      resolve(true);
    });
  }

  razorpayFailedPayment(error) {
    this.disablePayBtn = false;
    this.loading = false;
    this.disableAll$.next(false);
    this.vars.isOrderLoading = false;
    this.vars.tyParams = {
      ...this.vars.tyParams,
      razorpay_payment_id: error?.metadata?.payment_id || '',
      razorpay_order_id: error?.metadata?.order_id || '',
      source: error?.source || '',
      reason: error?.description || ''
    }
    this.util.openSnackBar(error?.description, 'error');
    this.getOrderStatusAfterPayment();
  }

  getOrderStatusAfterPayment(data = this.vars.tyParams) {
    const url = environment.APP.API_URLS.GATEWAY_RESPONSE(data?.oi);
    this.api.request('post', url, {}, data).subscribe({
      next: (success) => {
        console.log('success: ', success);
        this.util.router.navigate(['/new/payment-route'], { queryParams: this.vars.tyParams });
      },
      error: (err) => {
        this.paymentFailedEvent(err?.error);
      }
    });
  }

  paymentFailedEvent(error: any) {
    const ePayload = {
      eventName: 'payment_response',
      event_type: 'payment',
      page_name: 'payment-redirect-page',
      info_1: 'failed; ' + error?.message,
      info_2: this.vars.tyParams['oi'],
      info_3: JSON.stringify(this.vars.tyParams)
    };
    this.events.sendSystemEvent(ePayload).subscribe(_ => _);
  }

  async otdToSip(order?) {
  }

  goToThankYouPage(order: IOrder) {
    this.zone.run(() => {
      const url = this.vars.selectedKCart?.length > 0 ? '/new/kettokart/thankyou' : '/new/thankyou';
      this.util.router.navigate([url], { queryParams: this.vars.tyParams }).then(() => {
        this.resetPaymentConfig();
        this.dialog.closeAll();
        this.sheet.dismiss();
        this.util.storage.deleteFromSession(order.order_id);
      });
    });
  }

  sendLead(data: any) {
    const { donor_email, donor_extension, donor_phone, donor_name, subject } = data || {};
    const payload = {
      email: donor_email || this.userData?.email || '',
      phone: donor_phone || this.userData?.phone_1 || '',
      extension: donor_extension || this.userData?.extension || '',
      created_date: new Date(),
      name: donor_name || this.userData?.full_name || '',
      lead_name: donor_name || this.userData?.full_name || '',
      frequency: this.userData?.aggdonationall?.funds,
      final_url: this.vars.pageName,
      source: this.vars.pageName,
      page: this.vars.pageName,
      type: 'ketto-campaign-lead',
      mail_to: this.vars.leadMailTo,
      subject: subject
    };
    this.api.request('post', environment.APP.API_URLS.GET_LEAD, { showError: 'false' }, payload).subscribe(_ => _);
  }

  contributeInitiatedEvents(config: IPaymentConfig, cartRes?: ICartResponse) {
    return new Promise((resolve, reject) => {
      try {
        if (config.isContributeInitHit) { resolve(true); return; }
        const { cart } = cartRes || {};
        const { fundraiser } = this.vars;
        const claverTapProperty: any = {
          'Position': config?.possition || 1,
          'Custom Tag': fundraiser.custom_tag,
          'Cart ID': cart?.id || '',
          'Recurring': cart?.recurring ? true : false
        };
        if (cart?.recurring) {
          claverTapProperty['SIP Acquisition page name'] = this.util.toTitleCase(cart.onlySipCauseArr) + ' SIP';
        }
        if (this.vars.selected_insurance) {
          claverTapProperty['Plan Type'] = this.vars.selected_insurance.plan_text;
          claverTapProperty['Plan Validity'] = this.util.toTitleCase(this.vars.selected_insurance.frequency);
        }
        this.events.claverTapPush('Contribution Initiated', claverTapProperty);

        const dataLayer = {
          'event': 'Contribution Initiated',
          'event_category': 'Contribution Initiated',
          'event_action': 'click',
          'event_label': fundraiser?.title,
          'Campaign_custom_tag': fundraiser?.custom_tag,
          'Campaign_cause_id': fundraiser?.cause_id,
          'Campaign_id': fundraiser?.id,
          'Campaign_Type': this.util.getCampaignTypeFromId(fundraiser?.parent_cause_id),
          'position': config?.possition || 1,
          sip: cart?.recurring ? true : false
        };
        this.events.gtmPush(dataLayer);

        const ePayload = {
          eventName: 'Contribution Initiated',
          event_type: 'campaign',
          event_type_id: fundraiser?.id,
          info_1: 'possition',
          info_2: config?.possition || 1,
          info_3: this.vars.pageName
        };
        if (cart?.recurring) {
          ePayload.info_1 = this.util.arrayTostring(cart.onlySipCauseArr, ',');
        }
        this.events.sendSystemEvent(ePayload).subscribe(_ => _);
        resolve(true);
      } catch (error) {
        reject(false);
      }
    });
  }

  eventAfterCreatingCart(config: IPaymentConfig, cartRes: ICartResponse) {
    return new Promise((resolve, reject) => {
      try {
        const { campaign, cart } = cartRes;
        const contributeData = cart?.recurring ? this.sipContributeRes : this.otdContributeRes;
        const ePayload = {
          eventName: 'Contribution Cart',
          event_type: 'campaign',
          event_type_id: campaign?.id,
          info_1: '',
          info_2: cart.id,
          info_3: `${this.vars.pageName}`,
        };
        if (this.vars.selectedKCart) {
          ePayload.info_1 = this.getSelectedProductAsInfo();
        } else if (contributeData?.slabs) {
          ePayload.info_1 = `${JSON.stringify(contributeData.slabs)}/${cart.donated_amount}`;
        }
        if (cart?.recurring) {
          ePayload.info_1 = `${this.util.arrayTostring(cart?.onlySipCauseArr, ',')}, ${ePayload.info_1}`;
        }
        if (this.vars.fundraiser?.is_multi_patient_campaign && ['stories', 'mini_story'].includes(this.vars.pageName)) {
          ePayload.info_3 += this.vars.donateMultiPatient ? ',multi_p_enabled' : ',multi_p_disabled';
        }
        this.events.sendSystemEvent(ePayload).subscribe(_ => _);
        // Criteo Basket/Cart dataLayer of GTM push
        const dataLayerCartCreate = {
          event: 'crto_basketpage',
          currency: cart.currency,
          crto: {
            email: cart.donor_email, // can be empty string if email not known
            products: [{
              id: campaign?.id,
              price: cart?.donated_amount,
              quantity: '1'
            }] // add new object for each product added to cart
          }
        };
        this.events.gtmPush(dataLayerCartCreate);
        /**Claver tap push */
        const claverTapProperty = {
          'id': campaign.id,
          'Step': 1,
          'Name': cart.donor_name,
          'Email': cart.donor_email,
          'NC Email ID': cart.donor_email,
          'Type_AB': 'typeAB',
          'Custom Tag': campaign.custom_tag,
          'Parent Cause Id': campaign.parent_cause_id,
          'Page Name': this.vars.pageName,
          'iso_currency': cart.currency,
          'amount': cart.donated_amount - (cart?.tip_amount || 0),
          'TipAmount': cart?.tip_amount || 0,
          'Cart ID': cart.id,
          'Recurring': cart?.recurring ? true : false
        };
        if (cart.reward_id) {
          claverTapProperty['Amount'] = cart.donated_amount_inr;
          claverTapProperty['Reward'] = cart.reward_id;
        }
        if (cart?.recurring) claverTapProperty['SIP Acquisition page name'] = this.util.toTitleCase(cart.onlySipCauseArr || 'medical') + ' SIP';
        this.events.claverTapPush('New Contribution', claverTapProperty);

        const campaignType = `${this.util.getCampaignTypeFromId(campaign.parent_cause_id)}`;
        // Checkout/Cart create gtm event
        const gtmCheckout = {
          'event': 'addToCart',
          'sip': cart?.recurring || false,
          'value': cart.donated_amount_inr,
          'currency': cart.currency,
          'ecommerce': {
            'add': {
              'products': [{
                'name': `${campaign.custom_tag}`, // Campaign Name
                'id': `${campaign.id}`, // Campaign ID.
                'category': campaignType,  // Campaign Type
                'variant': `${campaign.cause_id}`,
                'price': cart.donated_amount_inr,
                'quantity': 1,
                'dimension5': '', // cd_Fundraiser Attributes,
                'dimension6': cart.currency,
                'dimension7': `${campaign.cause_id}`, // cd_Campaign Cause ID
                'dimension8': `${campaign.custom_tag}`, // cd_Campaign Custom Tag
                'dimension9': `${campaign.id}`, // cd_Campaign ID
                'dimension10': campaignType, // cd_Campaign Type
                'dimension15': cart.donated_amount_inr, // cd_Donation Amount
                'dimension16': this.vars.selectTipPercentage || '', // cd_Tip Percentage
                'dimension17': cart.tip_amount_inr, // cd_Tip Amount,
              }]
            }
          }
        };
        this.events.gtmPush(gtmCheckout);

        if (this.vars.fundraiser?.is_multi_patient_campaign && ['stories', 'mini_story'].includes(this.vars.pageName)) {
          const ePayload = {
            eventName: 'multi_patient_dropdown_shown',
            info_1: this.vars.donateMultiPatient ? 'enabled' : 'disabled',
            event_type_id: this.vars?.fundraiser?.id
          }
          this.events.sendSystemEvent(ePayload).subscribe(_ => _);
        }
        resolve(true);
      } catch (error) {
        reject(false);
      }
    });
  }

  eventAfterCreatingOrder(order: IOrder, cartData: ICartResponse) {
    return new Promise((resolve, reject) => {
      try {
        const { cart, campaign } = cartData;
        const contributeData = cart?.recurring ? this.sipContributeRes : this.otdContributeRes;
        const claverTap = {
          'id': order?.campaign_id,
          'Step': 3,
          'Name': cart.donor_name,
          'Email': cart.donor_email,
          'Amount': order?.donated_amount_local,
          'Payment Mode': order?.payment_mode,
          'Type_AB': 'typeAB',
          'Page Name': this.vars.pageName || '',
          'TipAmount': order?.tip_amount_local,
          'Iso Currency': order?.iso_currency,
          'Cart ID': cart.id,
          'Custom Tag': campaign.custom_tag,
          'Parent Cause Id': campaign.parent_cause_id,
          'Recurring': cart?.recurring ? true : false
        };
        if (cart?.reward_id) claverTap['Reward'] = cart.reward_id;
        if (cart?.recurring) claverTap['SIP Acquisition page name'] = this.util.toTitleCase(cart?.onlySipCauseArr || 'medical') + ' SIP';
        this.events.claverTapPush('New Contribution Step 3', claverTap);

        const ePayload = {
          'eventName': 'order_created',
          'event_type': 'campaign',
          'event_type_id': order.campaign_id,
          'info_1': '',
          'info_2': order.id,
          'info_3': this.vars.pageName,
        };
        if (this.vars.selectedKCart) {
          ePayload.info_1 = this.getSelectedProductAsInfo();
        } else if (contributeData?.slabs) {
          ePayload.info_1 = `${JSON.stringify(contributeData.slabs)}/${cart.donated_amount}`;
        }
        if (cart?.recurring) {
          ePayload.info_1 = `${this.util.arrayTostring(cart?.onlySipCauseArr, ',')}, ${ePayload.info_1}`;
          ePayload.info_3 += `; ${this.util.arrayTostring(cart?.onlySipCauseArr, ',')};`;
        }
        if (this.vars.fundraiser?.is_multi_patient_campaign && ['stories', 'mini_story'].includes(this.vars.pageName)) {
          ePayload.info_3 += this.vars.donateMultiPatient ? ',multi_p_enabled' : ',multi_p_disabled';
        }
        this.events.sendSystemEvent(ePayload).subscribe(_ => _);

        this.gtmEventForOrder(order, cartData);
      } catch (error) {
        reject(false);
      }
    });
  }

  gtmEventForOrder(order: IOrder, cartData: ICartResponse) {
    try {
      const { donated_amount, payment_mode, tip_amount_inr, iso_currency } = order;
      const { cart, campaign } = cartData;
      const gtmCheckout = {
        'event': 'checkout',
        'sip': cart?.recurring || false,
        'value': donated_amount,
        'currency': iso_currency,
        'ecommerce': {
          'checkout': {
            'actionField': { 'step': 2, 'option': payment_mode },
            'products': [{
              'name': campaign.custom_tag, // Campaign Name
              'id': campaign.id, // Campaign ID.
              'category': this.util.getCampaignTypeFromId(campaign.parent_cause_id),  // Campaign Type
              'variant': campaign.cause_id,
              'price': donated_amount,
              'quantity': 1,
              'dimension5': '', // cd_Fundraiser Attributes
              'dimension6': iso_currency,
              'dimension7': campaign.cause_id, // cd_Campaign Cause ID
              'dimension8': campaign.custom_tag, // cd_Campaign Custom Tag
              'dimension9': campaign.id, // cd_Campaign ID
              'dimension10': this.util.getCampaignTypeFromId(campaign.parent_cause_id), // cd_Campaign Type
              'dimension15': donated_amount, // cd_Donation Amount
              'dimension16': this.vars.selectTipPercentage || '', // cd_Tip Percentage
              'dimension17': tip_amount_inr // cd_Tip Amount
            }]
          }
        }
      };
      this.events.gtmPush(gtmCheckout);
    } catch (error) { }
  }

  totalSip(data: ISipCause[], currency: string): { total_amount?: number, only_cause_list?: string[], sip_count?: number, is_valid?: boolean } {
    if (!data.length) return undefined;
    let total = 0;
    let is_valid = true;
    const cause_list: string[] = [];
    if (typeof data === 'string') {
      data = [{ sip_cause: data, amount: 0 }];
    }
    try {
      data.forEach(element => {
        if (element?.sip_cause) {
          total += +element?.amount || 0;
          cause_list.push(element?.sip_cause);
        }
        if (this.sipContributeRes && this.sipContributeRes?.min_donation > +element.amount &&
          (!this.userData?.listsubscriptions?.[element?.sip_cause] || this.userData?.listsubscriptions?.[element?.sip_cause]?.amount > +element.amount)) {
          is_valid = false;
        }
      });
    } catch (error) { }
    if (cause_list.length === 0 && currency) {
      total = this.getMinSipAmount(currency)?.amount;
      cause_list.push(this.vars.default_sip_cause);
    }
    return { total_amount: total, only_cause_list: cause_list, sip_count: cause_list.length, is_valid: is_valid };
  }

  getMinSipAmount(currency: string) {
    const currencies: any = this.vars.min_sip_amounts;
    if (currency) {
      currency = currency?.toUpperCase();
      return {
        text: this.util.formatAmount(currencies[currency], currency),
        amount: currencies[currency],
        currency_symbol: Currency.find((item) => item.currency === currency)?.symbol
      };
    }
    return null;
  }

  getSelectedProductAsInfo() {
    let info1 = '';
    this.vars.selectedKCart.forEach((val, i) => {
      info1 += `${val?.name},${val?.unit_count},${+val?.per_unit_amount * +val?.unit_count}` + (i + 1 < this.vars.selectedKCart?.length ? ',' : '');
    });
    return info1;
  }

  getFailedOrderData(oi: string, config: IPaymentConfig) {
    const failedOrderData = this.util.storage.getFromSession(oi);
    if (failedOrderData) {
      if (failedOrderData?.cartData?.cart?.initPossition === config.possition) config.isContributeInitHit = true;
      config.failedPaymentStartFrom = failedOrderData?.cartData?.cart?.initPossition;
      config.failedOrderData = failedOrderData;
      const { currency, recurring, donated_amount, tip_amount, tip_amount_perc, donor_name, donor_email, donor_extension, donor_phone, donor_address, pan, donor_pincode, is_anonymous } = config.failedOrderData.cartData.cart;
      config.pCurrency = currency;
      config.userForm = {
        uName: donor_name || '',
        uEmail: donor_email || '',
        uExt: donor_extension || '',
        uPhone: donor_phone || '',
        uAddress: donor_address || '',
        uPan: pan || '',
        uPincode: donor_pincode || '',
        uAnonymous: is_anonymous
      }
      if (recurring) {
        config.isSip = true;
        config.sipSelectedAmount = donated_amount - (tip_amount || 0);
        config.sipTipAmount = tip_amount;
        config.sipTipAmountPerc = tip_amount_perc;
        this.sipCartRes = config.failedOrderData.cartData;
      } else {
        config.isSip = false;
        config.selectedAmount = donated_amount - (tip_amount || 0);
        config.tipAmount = tip_amount;
        config.tipAmountPerc = tip_amount_perc;
        this.cartRes = config.failedOrderData.cartData;
      }
    }
    return config;
  }
}
